import { toast } from '@heroui/react';
import { useMemo, useState } from 'react';

import type { SortingState, PaginationState, RowSelectionState } from '@hs/ui';
import { AlertDialog, Button, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconDotsVertical as MoreVertical,
  IconTrash as Trash2,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { UserEditorModal, type UserFormItem } from './components/UserEditorModal';

type User = UserFormItem & {
  id: string;
  createdAt: string;
};

function UsersActionToolbar({
  hasSelection,
  onCreate,
  onDelete,
}: {
  hasSelection: boolean;
  onCreate: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {hasSelection ? (
        <Button variant="danger" onPress={onDelete}>
          <Trash2 className="size-4" stroke={1.5} />
          删除
        </Button>
      ) : null}
      <Button variant="primary" onPress={onCreate}>
        <Plus className="size-4" stroke={1.5} />
        创建
      </Button>
    </div>
  );
}

export default function UserManagementPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [enumFilters, setEnumFilters] = useState<Record<string, string>>({});
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];

  const { data, isLoading } = trpc.users.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      username: searchKeyword || undefined,
      nickname: searchKeyword || undefined,
      ...(sortItem
        ? {
            sortBy: sortItem.id,
            sortOrder: sortItem.desc ? ('desc' as const) : ('asc' as const),
          }
        : {}),
      ...(enumFilters.status ? { status: enumFilters.status as 'ACTIVE' | 'LOCKED' | 'DISABLED' } : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const users = (data?.items ?? []) as User[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );
  const selectedCount = selectedIds.length;

  const deleteUserMutation = trpc.users.delete.useMutation();

  const handleCreate = () => {
    setEditingUser(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingUser(null);
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleBatchDeleteOpen = () => {
    if (selectedIds.length === 0) {
      toast.warning('请先选择要删除的用户');
      return;
    }
    setUserToDelete(null);
    setDeleteModalOpen(true);
  };

  const deleteTargetIds = userToDelete ? [userToDelete.id] : selectedIds;

  const deleteMessage = useMemo(() => {
    if (userToDelete) {
      return `确定要删除${userToDelete.username || '该用户'}吗？`;
    }
    if (selectedIds.length === 1) {
      const selected = users.find((item) => item.id === selectedIds[0]);
      return `确定要删除${selected?.username || '该用户'}吗？`;
    }
    return `确定要删除${selectedIds.length}个用户吗？`;
  }, [selectedIds, userToDelete, users]);

  const confirmDelete = async () => {
    if (deleteTargetIds.length === 0) return;
    setIsDeleting(true);
    try {
      for (const id of deleteTargetIds) {
        await deleteUserMutation.mutateAsync({ id });
      }
      toast.success(
        deleteTargetIds.length === 1 ? '用户删除成功' : `成功删除 ${deleteTargetIds.length} 个用户`
      );
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setRowSelection({});
      void utils.users.list.invalidate();
    } catch (error) {
      toast.danger('删除失败', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<User>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="users"
          data={users}
          recordCount={total}
          pageCount={pageCount}
          enableSelectColumn
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          enumFilters={enumFilters}
          onEnumFiltersChange={setEnumFilters}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <UsersActionToolbar
              hasSelection={selectedCount > 0}
              onCreate={handleCreate}
              onDelete={handleBatchDeleteOpen}
            />
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<User>>
            {({ row }) => {
              const user = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => handleEdit(user)}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete-row"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(user)}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <UserEditorModal
        open={editorModalOpen}
        user={editingUser}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingUser(null);
        }}
        onSuccess={handleEditorSuccess}
      />

      <AlertDialog
        isOpen={deleteModalOpen}
        onOpenChange={(open) => {
          setDeleteModalOpen(open);
          if (!open) setUserToDelete(null);
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>
                  {deleteTargetIds.length > 1 ? '确认批量删除' : '确认删除'}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>{deleteMessage}</AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  isDisabled={isDeleting}
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setUserToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={isDeleting}
                  onPress={() => void confirmDelete()}
                >
                  {isDeleting ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
